import produce from 'immer';
import { type AnyAction } from 'redux';

// Import types
import * as types from 'store/actions/types';

// Import helpers
import { LOCALSTORAGE_USER } from 'helpers/variables';

// Import ts types
import type { MissingAgreements } from 'types/api/agreements';

export enum IdentityVerificationState {
	Verified = 'verified',
	BeingVerified = 'being_verified',
	Unverified = 'unverified'
}

type User = {
	acceptedAgreements: boolean;
	deviceExists: boolean;
	limitMessage: string;
	isAuthenticated: boolean;
	isDeviceLimited: boolean;
	isLogged: boolean;
	parentalControlEnabled: boolean;
	hideUnavailable: boolean;
	subscriber_local_active: boolean;
	identity_verification_state: IdentityVerificationState
	rating: string | number;
	business_id: string | number;
	token: string;
	system: string;
	status: {
		agreements: MissingAgreements;
		missingAgreements: MissingAgreements;
		deviceName: string;
		limits: boolean;
	};
};

type State = Pick<User, 'acceptedAgreements' | 'deviceExists' | 'isAuthenticated' | 'isDeviceLimited' | 'isLogged'> & {
	authenticate: User;
	authError: {
		message: string;
		name: string;
		isError: boolean;
		errorCode: null;
		status: number;
	};
	devices: {
		limitMessage: string;
		data: any[];
		isLoaded: boolean;
		errorDevice: boolean;
	};
	error: boolean;
	isLoaded: boolean;
	isProductsLoaded: boolean;
	multipleAccount: {
		isMultipleAccount: boolean;
		accountList: { login: string; profile_name: string; system: string }[];
		token: string;
	};
	packets: {
		data: any[];
		isLoaded: boolean;
		isError: boolean;
	};
	products: any[];
	signOutError: boolean;
	signOutLoading: boolean;
	subscriberLocalActive: boolean;
};

const getInitialState = (): State => {
	const localstorage_user = localStorage.getItem(LOCALSTORAGE_USER);
	const user: User = localstorage_user ? JSON.parse(localstorage_user) : null;

	return {
		acceptedAgreements: user?.acceptedAgreements ?? false,
		authenticate: user,
		authError: {
			message: '',
			name: '',
			isError: false,
			errorCode: null,
			status: 200
		},
		deviceExists: user?.deviceExists ?? false,
		devices: {
			limitMessage: user?.limitMessage ?? '',
			data: [],
			isLoaded: false,
			errorDevice: false
		},
		error: false,
		isAuthenticated: user?.isAuthenticated ?? false,
		isDeviceLimited: user?.isDeviceLimited ?? false,
		isLoaded: false,
		isLogged: user?.isLogged ?? false,
		isProductsLoaded: false,
		multipleAccount: {
			isMultipleAccount: false,
			accountList: [],
			token: ''
		},
		packets: {
			data: [],
			isLoaded: false,
			isError: false
		},
		products: [],
		signOutError: false,
		signOutLoading: false,
		subscriberLocalActive: false
	};
};

const initialState = getInitialState();

export default (state = initialState, action: AnyAction) =>
	produce(state, (draft) => {
		switch (action.type) {
			// ******************* Authorize user *******************
			case types.AUTH_USER_LOADING:
				return { ...initialState };

			case types.AUTH_USER_SUCCESS:
				draft.authenticate = action.payload;
				draft.isAuthenticated = action.payload.isAuthenticated;
				draft.isDeviceLimited = action.payload.isDeviceLimited;
				draft.deviceExists = action.payload.deviceExists;
				draft.acceptedAgreements = action.payload.acceptedAgreements;
				draft.devices.limitMessage = action.payload.limitMessage;
				draft.isLogged = action.payload.isLogged;
				return;

			case types.AUTH_USER_ERROR:
				draft.authError.isError = true;
				draft.authError.name = action.payload.data.name || '';
				draft.authError.message = action.payload.data.message || '';
				draft.authError.status = action.payload.status;
				return;

			case types.AUTH_USER_APP_CHECK_TOKEN_ERROR:
				draft.authError.isError = true;
				draft.authError.message = action.payload?.message;
				return;

			case types.AUTH_USER_MULTIPLE_ACCOUNT:
				draft.multipleAccount.isMultipleAccount = true;
				draft.multipleAccount.token = action.payload.token;
				draft.multipleAccount.accountList = action.payload.status.accountList;
				return;

			case types.CLEAR_AUTH_USER_MULTIPLE_ACCOUNT:
				draft.multipleAccount = initialState.multipleAccount;
				return;

			// ******************* User details *******************
			case types.AUTH_USER_DETAILS_SUCCESS:
				draft.authenticate = action.payload;
				draft.subscriberLocalActive = action.payload.subscriberLocalActive;
				draft.isLoaded = true;
				return;

			case types.AUTH_USER_DETAILS_ERROR:
				draft.error = true;
				return;

			// ******************* User packets *******************
			case types.FETCH_USER_PACKETS_SUCCESS:
				draft.packets.isLoaded = true;
				draft.packets.data = action.payload.packets;
				draft.products = action.payload.products.data;
				draft.isProductsLoaded = true;
				return;

			case types.FETCH_USER_PACKETS_ERROR:
				draft.packets.isLoaded = true;
				draft.isProductsLoaded = true;
				draft.packets.isError = true;
				return;

			// ******************* User products *******************
			case types.FETCH_USER_PRODUCTS_UUIDS_LOADING:
				draft.isProductsLoaded = false;
				return;
			case types.FETCH_USER_PRODUCTS_UUIDS_SUCCESS:
				draft.isProductsLoaded = true;
				draft.products = action.payload;
				return;
			case types.FETCH_USER_PRODUCTS_UUIDS_ERROR:
				draft.isProductsLoaded = true;
				draft.products = [];
				return;

			// ******************* Fetch user agreements *******************
			case types.AUTH_UPDATE_AGREEMENTS_SUCCESS:
				draft.isLogged = true;
				return;

			case types.AUTH_UPDATE_AGREEMENTS_ERROR:
				draft.authError.status = action.payload.status;
				draft.authError.isError = true;
				draft.authError.errorCode = action.payload.errorCode;
				return;

			// ******************* Fetch user devices *******************
			case types.AUTH_FETCH_USER_DEVICES_LOADING:
				draft.devices.isLoaded = false;
				return;

			case types.AUTH_FETCH_USER_DEVICES_SUCCESS:
				draft.devices.isLoaded = true;
				draft.devices.data = action.payload;
				return;

			case types.AUTH_FETCH_USER_DEVICES_ERROR:
				draft.devices.errorDevice = true;
				draft.authError.status = action.payload.status;
				draft.authError.isError = true;
				draft.authError.errorCode = action.payload.errorCode;
				return;

			// ******************* Update user device *******************
			case types.AUTH_UPDATE_USER_DEVICES_SUCCES:
				draft.isDeviceLimited = !action.payload;
				draft.deviceExists = action.payload;
				draft.isLogged = draft.acceptedAgreements;
				return;

			case types.AUTH_UPDATE_USER_DEVICES_ERROR:
				draft.devices.errorDevice = true;
				draft.authError.status = action.payload.status;
				draft.authError.isError = true;
				draft.authError.name = action.payload.data.name || '';
				draft.authError.message = action.payload.data.message || '';
				return;

			// ******************* Update user device *******************
			case types.AUTH_USER_CLEAR:
				return { ...getInitialState() };

			// ******************* Set unavailable channels *******************
			case types.SET_USER_UNAVAILABLE_CHANNELS_SUCCESS:
				draft.authenticate.hideUnavailable = action.payload;
				return;

			case types.SET_USER_UNAVAILABLE_CHANNELS_ERROR:
				draft.error = true;
				return;

			// ******************* Set user in local  *******************
			case types.SET_USER_IN_LOCAL:
				draft.authenticate.subscriber_local_active = action.payload;
				return;

			// ******************* Sign out *******************
			case types.SIGN_OUT_LOADING:
				draft.signOutLoading = true;
				return;

			case types.SIGN_OUT:
				return { ...getInitialState() };

			case types.SIGN_OUT_ERROR:
				draft.signOutLoading = false;
				draft.signOutError = true;
				return;

			default:
				return state;
		}
	});
